import { AudienceItem, AudiencePlatformItem, CountryItem } from '@digiseg-labs/api-client';
import { ArrowRightIcon, CopyIcon, TargetIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Flex, Heading } from '@radix-ui/themes';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { formatLargeNumber } from '../../utils/number';
import { shouldHidePlatformId } from '../../utils/platformIdVisibility';
import AudienceRecommendationModal from '../AudienceRecommendationModal';
import { getCategoryIcon } from '@digiseg-labs/design-system';
import ShareMenu from './ShareMenu';

type Props = {
  audience: AudienceItem;
  categoryName: string;
  categoryCode: string;
  countries: CountryItem[];
  platforms: AudiencePlatformItem[];
  selectedCountry: CountryItem | undefined;
  selectedPlatform: AudiencePlatformItem | undefined;
  onCountrySelected: (country: CountryItem | undefined) => void;
  onPlatformSelected: (platform: AudiencePlatformItem | undefined) => void;
  variant?: 'default' | 'search';
  onCategorySelected?: (categoryCode: string) => void;
  buildShareUrl: (options: {
    categoryCode?: string;
    audienceCode?: string;
    includeCountry?: boolean;
    includePlatform?: boolean;
  }) => string;
  isModalOpen?: boolean;
  onModalOpenChange?: (audienceCode: string, open: boolean) => void;
};

const AudienceListItem = ({
  audience,
  categoryName,
  categoryCode,
  countries,
  platforms,
  selectedCountry,
  selectedPlatform,
  onCountrySelected,
  onPlatformSelected,
  variant = 'default',
  onCategorySelected,
  buildShareUrl,
  isModalOpen = false,
  onModalOpenChange,
}: Props) => {
  const isSearchVariant = variant === 'search';
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const CategoryIcon = useMemo(
    () => getCategoryIcon(categoryCode, categoryName),
    [categoryCode, categoryName],
  );
  const hidePlatformId = shouldHidePlatformId(selectedPlatform);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);
  
  const householdsDisplay = useMemo(() => {
    if (!selectedCountry) return null;
    const value = audience.reach_stats?.num_households;
    if (typeof value !== 'number') return '–';
    const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;
    return formatLargeNumber(value, locale);
  }, [audience, selectedCountry]);

  const handleCopyId = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!audience.platform_code || typeof navigator === 'undefined' || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(audience.platform_code);
      setIsCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy ID', error);
    }
  };

  const triggerClassNames = [
    'audience-list-item',
    isSearchVariant ? 'audience-list-item--search' : 'audience-list-item--default',
  ].join(' ');

  const trigger = (
    <Flex
      align="center"
      justify="between"
      className={triggerClassNames}
    >
      <Flex align="center" gap="3" className="audience-item-main">
        <IconContainer>
          <CategoryIcon size={16} aria-hidden="true" />
        </IconContainer>
        <Box className="audience-item-details">
          {isSearchVariant ? (
            <Flex align="center" gap="3" className="min-width-0">
              <Heading size="2" className="text-truncate">
                {audience.display_name}
              </Heading>
              <Badge
                size="2"
                color="green"
                className={onCategorySelected ? 'category-pill clickable' : 'category-pill'}
                role={onCategorySelected ? 'button' : undefined}
                aria-label={onCategorySelected ? `Show audiences in ${categoryName}` : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  if (onCategorySelected) {
                    onCategorySelected(categoryCode);
                  }
                }}
              >
                {categoryName}
              </Badge>
            </Flex>
          ) : (
            <Heading size="2" className="text-truncate">
              {audience.display_name}
            </Heading>
          )}
        </Box>
      </Flex>
      <Flex align="center" gap="2" className="audience-actions">
        {selectedPlatform && audience.platform_code && !hidePlatformId && (
          <Button
            size="1"
            variant="soft"
            color="iris"
            onClick={handleCopyId}
            className="copy-button"
          >
            {isCopied ? (
              'Copied!'
            ) : (
              <>
                <CopyIcon aria-hidden="true" />
                ID: {audience.platform_code}
              </>
            )}
          </Button>
        )}
        {householdsDisplay && (
          <Badge
            color="green"
            variant="soft"
            size="2"
            className="audience-reach-badge"
          >
            <TargetIcon width="16" height="16" />
            {householdsDisplay}
          </Badge>
        )}
        <ArrowPill />
      </Flex>
    </Flex>
  );

  return (
    <AudienceRecommendationModal
      audienceItem={audience}
      audienceCode={audience.code}
      categoryName={categoryName}
      countries={countries}
      platforms={platforms}
      selectedCountry={selectedCountry}
      selectedPlatform={selectedPlatform}
      onCountrySelected={onCountrySelected}
      onPlatformSelected={onPlatformSelected}
      trigger={trigger}
      open={isModalOpen}
      onOpenChange={(openState) => {
        onModalOpenChange?.(audience.code, openState);
      }}
      shareControls={(
        <ShareMenu
          target={{ type: 'audience', code: audience.code, name: audience.display_name, categoryCode }}
          selectedCountry={selectedCountry}
          selectedPlatform={selectedPlatform}
          buildShareUrl={buildShareUrl}
        />
      )}
    />
  );
};

const IconContainer = ({ children }: { children: ReactNode }) => (
  <Box className="audience-icon">
    {children}
  </Box>
);

const ArrowPill = () => (
  <Box className="audience-arrow">
    <ArrowRightIcon />
  </Box>
);

export default AudienceListItem;
